import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import { clearAuth } from '../auth';
import ProviderMap from '../components/map/ProviderMap';
import { geocodeTechnicians } from '../services/geocoding';
import '../styles/HirerDashboard.css';

const CATEGORIES = [
  { label: 'Plumbers',         search: 'plumber' },
  { label: 'Electricians',     search: 'electrician' },
  { label: 'HVAC Technician',  search: 'hvac' },
  { label: 'Painter',          search: 'painter' },
  { label: 'Locksmith',        search: 'locksmith' },
];

const DISPLAY_SEPARATION_METERS = 120;
const EARTH_RADIUS_METERS = 6371000;

function offsetCoordinates(lat, lng, distanceMeters, bearingDegrees) {
  const angularDistance = distanceMeters / EARTH_RADIUS_METERS;
  const bearing = (bearingDegrees * Math.PI) / 180;

  const lat1 = (lat * Math.PI) / 180;
  const lng1 = (lng * Math.PI) / 180;

  const sinLat1 = Math.sin(lat1);
  const cosLat1 = Math.cos(lat1);
  const sinAngular = Math.sin(angularDistance);
  const cosAngular = Math.cos(angularDistance);

  const lat2 = Math.asin(
    sinLat1 * cosAngular + cosLat1 * sinAngular * Math.cos(bearing)
  );

  const lng2 = lng1 + Math.atan2(
    Math.sin(bearing) * sinAngular * cosLat1,
    cosAngular - sinLat1 * Math.sin(lat2)
  );

  return {
    lat: (lat2 * 180) / Math.PI,
    lng: ((((lng2 * 180) / Math.PI) + 540) % 360) - 180,
  };
}

function applyDisplayOffsets(technicians = []) {
  const groups = new Map();

  technicians.forEach((tech) => {
    if (!Number.isFinite(tech.latitude) || !Number.isFinite(tech.longitude)) return;
    const key = `${Number(tech.latitude).toFixed(6)}|${Number(tech.longitude).toFixed(6)}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(tech);
  });

  const displayCoordsById = new Map();

  groups.forEach((group) => {
    if (group.length === 1) {
      const only = group[0];
      displayCoordsById.set(only.id, {
        displayLatitude: Number(only.latitude),
        displayLongitude: Number(only.longitude),
      });
      return;
    }

    const centerLat = Number(group[0].latitude);
    const centerLng = Number(group[0].longitude);
    const sortedGroup = [...group].sort((a, b) => Number(a.id) - Number(b.id));

    sortedGroup.forEach((tech, index) => {
      const ring = Math.floor(index / 6) + 1;
      const ringStart = (ring - 1) * 6;
      const ringCount = Math.min(6, sortedGroup.length - ringStart);
      const inRingIndex = index - ringStart;
      const angleStep = 360 / ringCount;
      const angle = inRingIndex * angleStep;
      const distance = DISPLAY_SEPARATION_METERS * ring;
      const shifted = offsetCoordinates(centerLat, centerLng, distance, angle);

      displayCoordsById.set(tech.id, {
        displayLatitude: shifted.lat,
        displayLongitude: shifted.lng,
      });
    });
  });

  return technicians.map((tech) => {
    const display = displayCoordsById.get(tech.id);
    if (!display) return tech;
    return { ...tech, ...display };
  });
}

function HirerDashboard() {
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [recommended, setRecommended] = useState([]);
  const [mappedTechnicians, setMappedTechnicians] = useState([]);
  const [selectedTechId, setSelectedTechId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingRec, setLoadingRec] = useState(true);
  const [mapping, setMapping] = useState(false);
  const navigate = useNavigate();
  const searchTimer = useRef(null);

  // ── Fetch helpers ──

  const fetchTechnicians = useCallback(async (searchTerm) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/technician/search?q=${encodeURIComponent(searchTerm)}`
      );
      if (res.ok) {
        const data = await res.json();
        setTechnicians(data.technicians || []);
      }
    } catch {
      console.error('Failed to fetch technicians');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRecommended = useCallback(async (category) => {
    setLoadingRec(true);
    try {
      const res = await fetch(
        `${API_BASE_URL}/technician/recommended?category=${encodeURIComponent(category || '')}&limit=10`
      );
      if (res.ok) {
        const data = await res.json();
        setRecommended(data.technicians || []);
      }
    } catch { /* ignore */ }
    finally { setLoadingRec(false); }
  }, []);

  // ── Build the effective search term (category + free-text) ──

  const buildSearchTerm = useCallback((category, freeText) => {
    const parts = [];
    if (category) parts.push(category);
    if (freeText.trim()) parts.push(freeText.trim());
    return parts.join(' ');
  }, []);

  // ── Initial load: all technicians + all recommended ──

  useEffect(() => {
    fetchTechnicians('');
    fetchRecommended('');
  }, [fetchTechnicians, fetchRecommended]);

  // ── When category changes, refetch both lists immediately ──

  useEffect(() => {
    const catSearch = CATEGORIES.find(c => c.label === activeCategory)?.search || '';
    const term = buildSearchTerm(catSearch, query);
    fetchTechnicians(term);
    fetchRecommended(catSearch);
  }, [activeCategory]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── When free-text search changes, debounce and refetch technicians ──

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      const catSearch = CATEGORIES.find(c => c.label === activeCategory)?.search || '';
      const term = buildSearchTerm(catSearch, query);
      fetchTechnicians(term);
    }, 350);
    return () => clearTimeout(searchTimer.current);
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Geocode for map whenever technicians list updqates ──

  useEffect(() => {
    let cancelled = false;
    const mapResults = async () => {
      setMapping(true);
      try {
        const withCoords = await geocodeTechnicians(technicians);
        if (!cancelled) setMappedTechnicians(applyDisplayOffsets(withCoords));
      } catch {
        if (!cancelled) setMappedTechnicians([]);
      } finally {
        if (!cancelled) setMapping(false);
      }
    };
    mapResults();
    return () => { cancelled = true; };
  }, [technicians]);

  // ── Handlers ──

  const handleCategoryClick = (cat) => {
    if (activeCategory === cat.label) {
      setActiveCategory('');
      setQuery('');
    } else {
      setActiveCategory(cat.label);
      setQuery('');
    }
  };

  const handleAllClick = () => {
    setActiveCategory('');
    setQuery('');
  };

  const displayType = (t) =>
    t.tech_type === 'other' ? (t.custom_tech_type || 'Other') : (t.tech_type || '—');

  const handleSignOut = () => {
    clearAuth();
    navigate('/');
  };

  const openConversation = (tech) => {
    navigate('/hirer/messages', {
      state: {
        startConversationWith: {
          id: tech.id,
          name: tech.full_name,
          role: 'technician',
        },
      },
    });
  };

  // ── Render helpers ──

  const renderTechCard = (t, showCategories = false) => (
    <div
      className={`hirer-card${selectedTechId === t.id ? ' hirer-card--selected' : ''}`}
      key={t.id}
    >
      <div>
        <p className="hirer-card__name">{t.full_name}</p>
        <div className="hirer-card__meta">
          <span className="hirer-chip">{displayType(t)}</span>
          {(t.categories || []).map((cat) => (
            <span key={`${t.id}-${cat}`} className="hirer-chip hirer-chip--cat">{cat}</span>
          ))}
          <span className="hirer-chip">📍 {t.city}, {t.province}</span>
          {t.avg_rating && (
            <span className="hirer-chip hirer-chip--rating">
              ★ {t.avg_rating} ({t.total_ratings})
            </span>
          )}
          {showCategories && t.categories?.length > 0 && t.categories.map((c) => (
            <span key={c} className="hirer-chip hirer-chip--cat">{c}</span>
          ))}
        </div>
      </div>
      <div className="hirer-card__actions">
        <button className="hirer-btn" onClick={() => navigate(`/hirer/technician/${t.id}`)}>
          View
        </button>
        <button className="hirer-btn hirer-btn--primary" onClick={() => openConversation(t)}>
          Contact
        </button>
      </div>
    </div>
  );

  const panelTitle = activeCategory || 'All Technicians';

  return (
    <div className="hirer-dashboard">
      <header className="hirer-topbar">
        <span className="hirer-topbar__logo">SkillArea</span>
        <div className="hirer-topbar__actions">
          <button className="hirer-topbar__btn" onClick={() => navigate('/hirer/messages')}>✉ Messages</button>
          <button className="hirer-topbar__btn" onClick={() => navigate('/hirer/support')}>🎧 Support</button>
          <button className="hirer-topbar__btn hirer-topbar__btn--ghost" onClick={handleSignOut}>Sign Out</button>
        </div>
      </header>

      {/* ── Category Browsing Bar ── */}
      <div className="hirer-catbar">
        <button
          className={`hirer-catbar__item${activeCategory === '' ? ' hirer-catbar__item--active' : ''}`}
          onClick={handleAllClick}
        >
          All
        </button>
        {CATEGORIES.map((cat) => (
          <button
            key={cat.label}
            className={`hirer-catbar__item${activeCategory === cat.label ? ' hirer-catbar__item--active' : ''}`}
            onClick={() => handleCategoryClick(cat)}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* ── Map Section (full width) ── */}
      <section className="hirer-dashboard__panel hirer-dashboard__mapSection">
        <div className="hirer-dashboard__panelHeader">
          <h2 className="hirer-dashboard__title">{panelTitle}</h2>
          <div className="hirer-dashboard__search">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={activeCategory ? `Search within ${activeCategory}…` : 'Search name, category, area…'}
              aria-label="Search technicians"
            />
          </div>
        </div>
        <div className="hirer-dashboard__map" aria-label="Technician map">
          <ProviderMap
            technicians={mappedTechnicians}
            onSelectTechnician={(tech) => setSelectedTechId(tech.id)}
          />
          <div className="hirer-dashboard__mapHint">
            {mapping
              ? 'Mapping technicians…'
              : `${mappedTechnicians.length} technician${mappedTechnicians.length === 1 ? '' : 's'} on map`}
          </div>
        </div>
      </section>

      {/* ── Search Results + Recommended (side by side) ── */}
      <div className="hirer-dashboard__top">
        <section className="hirer-dashboard__panel">
          <div className="hirer-dashboard__panelHeader">
            <h2 className="hirer-dashboard__title">
              {activeCategory ? activeCategory : 'Search Results'}
            </h2>
          </div>
          {loading ? (
            <div className="hirer-empty">Loading…</div>
          ) : technicians.length === 0 ? (
            <div className="hirer-empty">
              No {activeCategory ? activeCategory.toLowerCase() : 'technicians'} found
              {query.trim() ? ` matching "${query.trim()}"` : ''}.
            </div>
          ) : (
            <div className="hirer-dashboard__list">
              {technicians.map((t) => renderTechCard(t))}
            </div>
          )}
        </section>

        {/* ── Recommended (only if 4-5 star technicians exist) ── */}
        {!loadingRec && recommended.length > 0 && (
          <section className="hirer-dashboard__panel">
            <div className="hirer-dashboard__panelHeader">
              <h2 className="hirer-dashboard__title">
                Recommended{activeCategory ? ` ${activeCategory}` : ''}
              </h2>
            </div>
            <div className="hirer-dashboard__list">
              {recommended.map((t) => renderTechCard(t, true))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

export default HirerDashboard;